import {
  CreateUserRequest,
  CreateUserResponse, DeviceSession,
  UpdateUserRequest,
  UpdateUserResponse,
  User
} from '@/types/model';
import { PaginatedResponse, apiClient, QueryParams } from '@/services/apiClient.ts';

export type UserListItem = Pick<User, 'id' | 'username' | 'fullName' | 'role' | 'credits' |
  'saleCredits' | 'expirationDate' | 'signIns' | 'accessCount' | 'status' | 'createdDate' | 'createdBy' | 'createdByUserId'>;

export interface UsersQueryParams extends QueryParams {
  fullName?: string;
  userName?: string;
  active?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
  user: User;
}

export interface ActiveUsersCountResponse {
  count: number;
}

export const userService = {
  async getUserList(params: QueryParams = {}): Promise<PaginatedResponse<UserListItem>> {
    return apiClient.getPaginated<UserListItem>('/users', { ...params, _view: 'list' });
  },

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    return apiClient.create<CreateUserRequest, CreateUserResponse>('/users', userData);
  },

  /**
   * Get all users
   */
  async getUsers(params: QueryParams = {}): Promise<PaginatedResponse<User>> {
    return apiClient.getPaginated<User>('/users', params);
  },

  /**
   * Get app users for display-now selection
   */
  async getAppUsers(params: QueryParams = {}): Promise<PaginatedResponse<User>> {
    return apiClient.getPaginated<User>('/users/app-search', params);
  },

  /**
   * Get a single user by ID
   */
  async getUserById(id: string): Promise<User> {
    return apiClient.getById<User>('/users', id);
  },

  /**
   * Get active app users count created by a given user
   */
  async getActiveUsersCountByCreator(userId: string): Promise<ActiveUsersCountResponse> {
    return apiClient.get<ActiveUsersCountResponse>(`/users/${userId}/active-users-count`);
  },

  /**
   * Get user devices/sessions
   *
   */
  async getUserDevices(userId: number): Promise<DeviceSession> {
    return apiClient.get<DeviceSession>(`/users/${userId}/device`);
  },

  async terminateUserDeviceSession(userId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/users/${userId}/device`);
  },

  /**
   * Update a user
   */
  async updateUser(id: string, userData: UpdateUserRequest): Promise<UpdateUserResponse> {
    return apiClient.update<UpdateUserRequest, UpdateUserResponse>('/users', id, userData);
  },

  /**
   * Delete a user
   */
  async deleteUser(id: number): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/users', id);
  },

  /**
   * Change user password
   */
  async changePassword( passwordData: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    return apiClient.put<ChangePasswordResponse>(`/users/me/password`, passwordData);
  },

  /**
   * Toggle user status (activate/deactivate)
   */
  async toggleUserStatus(userId: string): Promise<{ success: boolean; message: string; user: User }> {
    return apiClient.patch<{ success: boolean; message: string; user: User }>(`/users/${userId}/toggle-status`, {});
  },
};
